import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Trophy, Plus, Play, Users, Swords, ChevronDown, ChevronUp,
  Loader2, Check, AlertCircle, Crown, Medal, BookOpen, MessageSquare, Settings2,
  RotateCcw, Trash2, Square, Scale,
} from "lucide-react";
import type {
  Bot,
  NegotiationCase,
  RolePair,
  Tournament,
  TournamentGroup,
  TournamentJudgeSlot,
  TournamentMatch,
  TournamentStanding,
} from "@shared/schema";
import {
  TOURNAMENT_CLASSIC_JUDGE_SLOT_LABELS,
  TOURNAMENT_EXPRESS_JUDGE_SLOT_LABELS,
} from "@shared/schema";
import { useToast } from "@/hooks/use-toast";

/* ─── Tournament creation form (simplified: no global case/rolePair) ─── */

function slotsForFormat(format: "express" | "classic"): TournamentJudgeSlot[] {
  const labels = format === "classic"
    ? TOURNAMENT_CLASSIC_JUDGE_SLOT_LABELS
    : TOURNAMENT_EXPRESS_JUDGE_SLOT_LABELS;
  return labels.map(category => ({ judgeId: "", category }));
}

function CreateTournamentForm({ onCreated }: { onCreated: () => void }) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [groupA, setGroupA] = useState<string[]>(["", "", ""]);
  const [groupB, setGroupB] = useState<string[]>(["", "", ""]);
  const [duelFormat, setDuelFormat] = useState<"express" | "classic">("express");
  const [judgeSlots, setJudgeSlots] = useState<TournamentJudgeSlot[]>(() => slotsForFormat("express"));

  const { data: bots } = useQuery<Bot[]>({ queryKey: ["/api/bots"] });
  const judgeBots = useMemo(() => (bots || []).filter((b: any) => b.isJudge), [bots]);

  useEffect(() => {
    setJudgeSlots(slotsForFormat(duelFormat));
  }, [duelFormat]);

  const setGroupSlot = (group: "A" | "B", index: number, value: string) => {
    if (group === "A") {
      const copy = [...groupA];
      copy[index] = value;
      setGroupA(copy);
    } else {
      const copy = [...groupB];
      copy[index] = value;
      setGroupB(copy);
    }
  };

  // Collect all selected bot ids to prevent duplicates
  const allSelectedBotIds = [...groupA, ...groupB].filter(Boolean);

  const createMutation = useMutation({
    mutationFn: async () => {
      const participantsA = groupA.filter(Boolean);
      const participantsB = groupB.filter(Boolean);
      if (participantsA.length < 2 || participantsB.length < 2) {
        throw new Error("Минимум 2 участника в каждой группе");
      }
      const res = await apiRequest("POST", "/api/tournaments", {
        name: name || "Чемпионат",
        duelFormat,
        judges: judgeSlots,
        groups: [
          { name: "Группа A", participantIds: participantsA },
          { name: "Группа B", participantIds: participantsB },
        ],
      });
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tournaments"] });
      setName("");
      setGroupA(["", "", ""]);
      setGroupB(["", "", ""]);
      setDuelFormat("express");
      setJudgeSlots(slotsForFormat("express"));
      onCreated();
      toast({ title: "Чемпионат создан" });
    },
    onError: (err: any) => {
      toast({ title: "Ошибка", description: err.message, variant: "destructive" });
    },
  });

  const judgesComplete = judgeSlots.length > 0 && judgeSlots.every(s => s.judgeId.trim() !== "");

  const canCreate =
    groupA.filter(Boolean).length >= 2 &&
    groupB.filter(Boolean).length >= 2 &&
    new Set(allSelectedBotIds).size === allSelectedBotIds.length &&
    judgesComplete;

  const NONE_VALUE = "__none__";

  const renderBotSelect = (group: "A" | "B", index: number, value: string) => {
    const otherSelected = allSelectedBotIds.filter((_, i) => {
      const isCurrentSlot = group === "A" ? i === index : i === 3 + index;
      return !isCurrentSlot;
    });
    return (
      <Select
        value={value || NONE_VALUE}
        onValueChange={(v) => setGroupSlot(group, index, v === NONE_VALUE ? "" : v)}
      >
        <SelectTrigger className="h-9 text-sm" data-testid={`select-bot-${group}-${index}`}>
          <SelectValue placeholder="Выберите бота" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>— не выбрано —</SelectItem>
          {(bots || []).map(b => (
            <SelectItem
              key={b.id}
              value={b.id}
              disabled={otherSelected.includes(b.id)}
            >
              {b.avatar} {b.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  return (
    <Card className="p-5 space-y-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
        <Plus className="w-4 h-4" />
        Новый чемпионат
      </h2>

      <div className="space-y-1.5">
        <label className="text-xs text-muted-foreground">Название</label>
        <Input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Чемпионат переговорщиков"
          className="h-9 text-sm"
          data-testid="input-tournament-name"
        />
      </div>

      {/* Groups */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {(["A", "B"] as const).map(g => (
          <Card key={g} className="p-4 space-y-3 bg-muted/20">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              Группа {g}
            </h3>
            {[0, 1, 2].map(i => (
              <div key={i} className="space-y-1">
                <label className="text-xs text-muted-foreground">Участник {i + 1}{i < 2 ? " *" : ""}</label>
                {renderBotSelect(g, i, g === "A" ? groupA[i] : groupB[i])}
              </div>
            ))}
          </Card>
        ))}
      </div>

      <Card className="p-4 space-y-4 bg-muted/20">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Scale className="w-4 h-4 text-amber-400" />
          Формат поединков и состав судей
        </h3>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant={duelFormat === "express" ? "default" : "outline"}
            size="sm"
            className="text-xs"
            onClick={() => setDuelFormat("express")}
          >
            Экспресс — 5 судей (направляющие на переговоры)
          </Button>
          <Button
            type="button"
            variant={duelFormat === "classic" ? "default" : "outline"}
            size="sm"
            className="text-xs"
            onClick={() => setDuelFormat("classic")}
          >
            Классика — 7 судей (два тайма со сменой сторон)
          </Button>
        </div>
        {duelFormat === "classic" && (
          <p className="text-xs text-muted-foreground">
            Классика: один судья «доверяющие собственность», трое «направляющие на переговоры», один «устраивающиеся на работу» и ещё двое «направляющие на переговоры».
          </p>
        )}
        {duelFormat === "express" && (
          <p className="text-xs text-muted-foreground">
            Экспресс: пять судей в категории «направляющие на переговоры»; один тайм переговоров.
          </p>
        )}
        {judgeBots.length === 0 ? (
          <p className="text-xs text-amber-500 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Нет ботов с ролью судьи. Отметьте isJudge у ботов в базе или создайте судейских ботов.
          </p>
        ) : (
          <div className="space-y-2">
            {judgeSlots.map((slot, idx) => (
              <div key={`${duelFormat}-${idx}`} className="flex flex-col sm:flex-row sm:items-center gap-2">
                <span className="text-xs text-muted-foreground sm:w-64 shrink-0 leading-tight">
                  {slot.category}
                </span>
                <Select
                  value={slot.judgeId || NONE_VALUE}
                  onValueChange={(v) => {
                    const next = [...judgeSlots];
                    next[idx] = { ...next[idx], judgeId: v === NONE_VALUE ? "" : v };
                    setJudgeSlots(next);
                  }}
                >
                  <SelectTrigger className="h-9 text-sm flex-1" data-testid={`select-tournament-judge-${idx}`}>
                    <SelectValue placeholder="Выберите судью" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>— не выбрано —</SelectItem>
                    {judgeBots.map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.avatar} {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}
      </Card>

      {allSelectedBotIds.length !== new Set(allSelectedBotIds).size && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Один бот не может участвовать в нескольких слотах
        </p>
      )}

      {!judgesComplete && judgeBots.length > 0 && (
        <p className="text-xs text-amber-500 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Заполните все слоты судей для выбранного формата
        </p>
      )}

      <Button
        onClick={() => createMutation.mutate()}
        disabled={!canCreate || createMutation.isPending}
        className="w-full"
        data-testid="btn-create-tournament"
      >
        {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trophy className="w-4 h-4 mr-2" />}
        Создать чемпионат
      </Button>
    </Card>
  );
}

/* ─── Per-match case assignment component ─── */

function MatchCaseAssignment({
  match,
  botMap,
  cases,
  assignment,
  onChange,
}: {
  match: TournamentMatch;
  botMap: Map<string, Bot>;
  cases: NegotiationCase[];
  assignment: { caseId: string; rolePairId: string };
  onChange: (caseId: string, rolePairId: string) => void;
}) {
  const botA = botMap.get(match.participantAId);
  const botB = botMap.get(match.participantBId);
  const nameA = botA ? `${botA.avatar} ${botA.name}` : match.participantAId;
  const nameB = botB ? `${botB.avatar} ${botB.name}` : match.participantBId;

  const selectedCase = cases.find(c => c.id === assignment.caseId);
  const rolePairs: RolePair[] = selectedCase?.rolePairs || [];

  const NONE_VALUE = "__none__";

  return (
    <div className="rounded border border-border/50 p-3 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Swords className="w-3.5 h-3.5 text-muted-foreground" />
        <span>{nameA}</span>
        <span className="text-muted-foreground text-xs">vs</span>
        <span>{nameB}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Кейс</label>
          <Select
            value={assignment.caseId || NONE_VALUE}
            onValueChange={v => onChange(v === NONE_VALUE ? "" : v, "")}
          >
            <SelectTrigger className="h-8 text-xs" data-testid={`select-match-case-${match.id}`}>
              <SelectValue placeholder="Выберите кейс" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>— не выбрано —</SelectItem>
              {cases.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Ролевая пара</label>
          <Select
            value={assignment.rolePairId || NONE_VALUE}
            onValueChange={v => onChange(assignment.caseId, v === NONE_VALUE ? "" : v)}
            disabled={!assignment.caseId}
          >
            <SelectTrigger className="h-8 text-xs" data-testid={`select-match-rp-${match.id}`}>
              <SelectValue placeholder={assignment.caseId ? "Выберите пару" : "Сначала кейс"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>— не выбрано —</SelectItem>
              {rolePairs.map(rp => (
                <SelectItem key={rp.id} value={rp.id}>{rp.roleA} vs {rp.roleB}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {assignment.caseId && assignment.rolePairId && (
        <div className="flex items-center gap-1 text-xs text-green-400">
          <Check className="w-3 h-3" />
          Назначено
        </div>
      )}
    </div>
  );
}

/* ─── Tournament bracket/grid view ─── */

function TournamentView({
  tournament,
  onDeleted,
}: {
  tournament: Tournament;
  onDeleted?: () => void;
}) {
  const { toast } = useToast();
  const [isRunning, setIsRunning] = useState(false);
  const [matchLog, setMatchLog] = useState<string[]>([]);
  const [expandedDuel, setExpandedDuel] = useState<string | null>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const streamAbortRef = useRef<AbortController | null>(null);

  // Per-match case/rolePair assignments keyed by match id
  const [assignments, setAssignments] = useState<Record<string, { caseId: string; rolePairId: string }>>({});

  const { data: bots } = useQuery<Bot[]>({ queryKey: ["/api/bots"] });
  const { data: cases } = useQuery<NegotiationCase[]>({ queryKey: ["/api/cases"] });
  const { data: currentTournament, refetch } = useQuery<Tournament>({
    queryKey: ["/api/tournaments", tournament.id],
    staleTime: 0,
    refetchOnMount: "always",
    initialData: tournament,
  });

  const t = currentTournament || tournament;
  const botMap = new Map((bots || []).map(b => [b.id, b]));

  const getBotName = useCallback((id: string) => {
    const bot = (bots || []).find(b => b.id === id);
    return bot ? `${bot.avatar} ${bot.name}` : id;
  }, [bots]);

  useEffect(() => {
    return () => {
      streamAbortRef.current?.abort();
    };
  }, []);

  // Initialize assignments from existing match data (if already saved)
  const allMatches = t.groups.flatMap(g => g.matches);
  const hasPendingMatches = allMatches.some(m => m.status !== "completed");
  const initAssignments = () => {
    const init: Record<string, { caseId: string; rolePairId: string }> = {};
    for (const m of allMatches) {
      init[m.id] = {
        caseId: (assignments[m.id]?.caseId ?? m.caseId) || "",
        rolePairId: (assignments[m.id]?.rolePairId ?? m.rolePairId) || "",
      };
    }
    return init;
  };

  // Use initialized assignments (merge existing state with match data)
  const effectiveAssignments = (() => {
    const result: Record<string, { caseId: string; rolePairId: string }> = {};
    for (const m of allMatches) {
      result[m.id] = assignments[m.id] || { caseId: m.caseId || "", rolePairId: m.rolePairId || "" };
    }
    return result;
  })();

  const allAssigned = allMatches.every(m => {
    const a = effectiveAssignments[m.id];
    return a && a.caseId && a.rolePairId;
  });

  const setMatchAssignment = (matchId: string, caseId: string, rolePairId: string) => {
    setAssignments(prev => ({ ...prev, [matchId]: { caseId, rolePairId } }));
  };

  // Save assignments to backend
  const saveMutation = useMutation({
    mutationFn: async () => {
      const matchAssignments = Object.entries(effectiveAssignments).map(([matchId, a]) => ({
        matchId,
        caseId: a.caseId,
        rolePairId: a.rolePairId,
      }));
      await apiRequest("PATCH", `/api/tournaments/${t.id}/matches`, { assignments: matchAssignments });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tournaments", t.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/tournaments"] });
      refetch();
      toast({ title: "Кейсы назначены" });
    },
    onError: (err: any) => {
      toast({ title: "Ошибка", description: err.message, variant: "destructive" });
    },
  });

  const saveAssignmentsIfNeeded = useCallback(async () => {
    const matchAssignments = Object.entries(effectiveAssignments).map(([matchId, a]) => ({
      matchId,
      caseId: a.caseId,
      rolePairId: a.rolePairId,
    }));
    await apiRequest("PATCH", `/api/tournaments/${t.id}/matches`, { assignments: matchAssignments });
    await refetch();
  }, [t.id, effectiveAssignments, refetch]);

  const readSseStream = useCallback(async (res: Response) => {
    const reader = res.body?.getReader();
    if (!reader) throw new Error("Нет потока ответа");

    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const payload = line.slice(6);
        if (payload === "[DONE]") break;

        try {
          const event = JSON.parse(payload);
          if (event.type === "match_start") {
            setMatchLog(prev => [...prev, `⚔️ ${event.botAName} vs ${event.botBName}...`]);
          } else if (event.type === "match_complete") {
            const winText = event.winner === "draw"
              ? "Ничья"
              : `Победа ${getBotName(event.winner)}`;
            setMatchLog(prev => [...prev, `✅ ${event.scoreA}:${event.scoreB} — ${winText}`]);
            refetch();
          } else if (event.type === "tournament_complete") {
            setMatchLog(prev => [...prev, "🏆 Чемпионат завершён!"]);
            refetch();
          } else if (event.type === "tournament_paused") {
            setMatchLog(prev => [...prev, "⏸ Турнир на паузе"]);
            refetch();
          } else if (event.type === "tournament_resumed") {
            setMatchLog(prev => [...prev, "▶ Продолжение турнира..."]);
          } else if (event.type === "tournament_started") {
            setMatchLog(prev => [...prev, "▶ Старт турнира"]);
          } else if (event.type === "error") {
            setMatchLog(prev => [...prev, `❌ ${event.message}`]);
          }
        } catch {
          /* ignore parse errors */
        }
      }

      if (logRef.current) {
        logRef.current.scrollTop = logRef.current.scrollHeight;
      }
    }
  }, [getBotName, refetch]);

  const runStreamRequest = useCallback(async (path: "/start" | "/continue") => {
    const missingMatches = allMatches.filter(m => {
      const a = effectiveAssignments[m.id];
      return !a || !a.caseId || !a.rolePairId;
    });
    if (missingMatches.length > 0) {
      toast({ title: "Ошибка", description: "Назначьте кейс и ролевую пару на все встречи", variant: "destructive" });
      return;
    }

    try {
      await saveAssignmentsIfNeeded();
    } catch (err: any) {
      toast({ title: "Ошибка сохранения", description: err.message, variant: "destructive" });
      return;
    }

    streamAbortRef.current?.abort();
    const ac = new AbortController();
    streamAbortRef.current = ac;

    setIsRunning(true);
    if (path === "/start") setMatchLog([]);

    try {
      const API_BASE = "__PORT_5000__".startsWith("__") ? "" : "__PORT_5000__";
      const res = await fetch(`${API_BASE}/api/tournaments/${t.id}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: ac.signal,
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        throw new Error(errText || `HTTP ${res.status}`);
      }

      await readSseStream(res);
    } catch (err: any) {
      if (err?.name === "AbortError") {
        setMatchLog(prev => [...prev, "⏹ Остановка по запросу"]);
      } else {
        toast({ title: "Ошибка", description: err.message, variant: "destructive" });
      }
    } finally {
      setIsRunning(false);
      streamAbortRef.current = null;
      queryClient.invalidateQueries({ queryKey: ["/api/tournaments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tournaments", t.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/bots"] });
      refetch();
    }
  }, [t.id, allMatches, effectiveAssignments, toast, saveAssignmentsIfNeeded, readSseStream, refetch]);

  const startTournament = useCallback(() => runStreamRequest("/start"), [runStreamRequest]);
  const continueTournament = useCallback(() => runStreamRequest("/continue"), [runStreamRequest]);

  const stopTournament = useCallback(() => {
    streamAbortRef.current?.abort();
  }, []);

  const restartMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/tournaments/${t.id}/restart`, {});
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/tournaments", t.id] });
      await queryClient.invalidateQueries({ queryKey: ["/api/tournaments"] });
      await refetch();
      setMatchLog([]);
      toast({ title: "Турнир сброшен", description: "Можно снова назначить кейсы и нажать «Старт»" });
    },
    onError: (err: any) => {
      toast({ title: "Ошибка", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/tournaments/${t.id}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/tournaments"] });
      toast({ title: "Турнир удалён" });
      onDeleted?.();
    },
    onError: (err: any) => {
      toast({ title: "Ошибка", description: err.message, variant: "destructive" });
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            {t.name}
          </h2>
          <Badge
            variant={
              t.status === "completed"
                ? "default"
                : t.status === "active"
                  ? "secondary"
                  : t.status === "paused"
                    ? "outline"
                    : "outline"
            }
            className={`mt-1 text-xs ${t.status === "paused" ? "border-amber-500/40 text-amber-500" : ""}`}
          >
            {t.status === "setup"
              ? "Подготовка"
              : t.status === "active"
                ? "В процессе"
                : t.status === "paused"
                  ? "На паузе"
                  : "Завершён"}
          </Badge>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge variant="secondary" className="text-xs font-normal">
              {t.duelFormat === "classic"
                ? "Формат: классика (7 судей, 2 тайма)"
                : "Формат: экспресс (5 судей)"}
            </Badge>
            {(t.judges?.length ?? 0) > 0 && (
              <Badge variant="outline" className="text-xs font-normal">
                Панель судей: {t.judges!.length} чел.
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col items-stretch gap-2 sm:items-end">
          {(t.status === "setup" || t.status === "paused") && (
            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || !allAssigned}
                className="gap-1.5 text-xs"
                data-testid="btn-save-assignments"
              >
                {saveMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Сохранить кейсы
              </Button>
              {t.status === "setup" && (
                <Button
                  onClick={startTournament}
                  disabled={isRunning || !allAssigned}
                  className="gap-2"
                  data-testid="btn-start-tournament"
                >
                  {isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Старт
                </Button>
              )}
              {t.status === "paused" && hasPendingMatches && (
                <Button
                  onClick={continueTournament}
                  disabled={isRunning || !allAssigned}
                  className="gap-2"
                  data-testid="btn-continue-tournament"
                >
                  {isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Продолжить
                </Button>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-2 justify-end">
            {isRunning && (
              <Button
                variant="destructive"
                size="sm"
                className="gap-1.5"
                onClick={stopTournament}
                data-testid="btn-stop-tournament"
              >
                <Square className="w-3.5 h-3.5" />
                Остановить
              </Button>
            )}
            {!isRunning && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  disabled={restartMutation.isPending}
                  onClick={() => {
                    if (window.confirm("Сбросить все встречи и вернуть турнир в подготовку? Результаты будут удалены.")) {
                      restartMutation.mutate();
                    }
                  }}
                  data-testid="btn-restart-tournament"
                >
                  {restartMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                  Перезапустить
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 text-destructive hover:text-destructive"
                  disabled={deleteMutation.isPending}
                  onClick={() => {
                    if (window.confirm("Удалить турнир безвозвратно?")) {
                      deleteMutation.mutate();
                    }
                  }}
                  data-testid="btn-delete-tournament"
                >
                  {deleteMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  Удалить
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Setup: Per-match case assignment */}
      {(t.status === "setup" || t.status === "paused") && (
        <Card className="p-5 space-y-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <Settings2 className="w-4 h-4" />
            Назначение кейсов на встречи
          </h3>
          <p className="text-xs text-muted-foreground">
            Выберите кейс и ролевую пару для каждой встречи. Разные встречи могут использовать разные кейсы.
          </p>

          {!allAssigned && (
            <div className="flex items-center gap-1.5 text-xs text-amber-400">
              <AlertCircle className="w-3 h-3" />
              Назначьте кейсы на все встречи, чтобы начать чемпионат
            </div>
          )}

          {t.groups.map(group => (
            <div key={group.id} className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Users className="w-3.5 h-3.5 text-muted-foreground" />
                {group.name}
              </h4>
              {group.matches.map(match => (
                <MatchCaseAssignment
                  key={match.id}
                  match={match}
                  botMap={botMap}
                  cases={cases || []}
                  assignment={effectiveAssignments[match.id] || { caseId: "", rolePairId: "" }}
                  onChange={(caseId, rolePairId) => setMatchAssignment(match.id, caseId, rolePairId)}
                />
              ))}
            </div>
          ))}
        </Card>
      )}

      {/* Groups grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {t.groups.map((group, gi) => (
          <Card key={group.id} className="p-4 space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {group.name}
            </h3>

            {/* Participants */}
            <div className="space-y-1.5">
              {group.participantIds.map((pid, pi) => (
                <div
                  key={pid}
                  className="flex items-center gap-2 text-sm px-2 py-1.5 rounded bg-muted/30"
                  data-testid={`participant-${gi}-${pi}`}
                >
                  <span className="text-muted-foreground text-xs w-4">{pi + 1}.</span>
                  <span>{getBotName(pid)}</span>
                </div>
              ))}
            </div>

            {/* Matches */}
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Swords className="w-3 h-3" />
                Встречи
              </div>
              {group.matches.map((match) => (
                <MatchRow
                  key={match.id}
                  match={match}
                  botMap={botMap}
                  cases={cases || []}
                  expanded={expandedDuel === match.duelId}
                  onToggle={() => setExpandedDuel(expandedDuel === match.duelId ? null : (match.duelId || null))}
                />
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* Live log */}
      {matchLog.length > 0 && (
        <Card className="p-4 space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Ход чемпионата
          </h3>
          <div
            ref={logRef}
            className="max-h-48 overflow-y-auto space-y-1 text-sm font-mono bg-muted/20 rounded p-3"
            data-testid="tournament-log"
          >
            {matchLog.map((msg, i) => (
              <div key={i}>{msg}</div>
            ))}
          </div>
        </Card>
      )}

      {/* Final standings */}
      {t.status === "completed" && t.results.length > 0 && (
        <StandingsTable results={t.results} groups={t.groups} botMap={botMap} />
      )}
    </div>
  );
}

/* ─── Match row ─── */

function MatchRow({
  match,
  botMap,
  cases,
  expanded,
  onToggle,
}: {
  match: TournamentMatch;
  botMap: Map<string, Bot>;
  cases: NegotiationCase[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const botA = botMap.get(match.participantAId);
  const botB = botMap.get(match.participantBId);
  const nameA = botA ? `${botA.avatar} ${botA.name}` : match.participantAId;
  const nameB = botB ? `${botB.avatar} ${botB.name}` : match.participantBId;

  const isCompleted = match.status === "completed";
  const isWinA = match.winnerId === match.participantAId;
  const isWinB = match.winnerId === match.participantBId;
  const isDraw = match.winnerId === "draw";

  // Show assigned case info
  const assignedCase = match.caseId ? cases.find(c => c.id === match.caseId) : null;
  const assignedRp = assignedCase?.rolePairs?.find(rp => rp.id === match.rolePairId);

  // Load duel details if expanded
  const { data: duel } = useQuery({
    queryKey: ["/api/duels", match.duelId],
    enabled: !!match.duelId && expanded,
    staleTime: 0,
  });

  return (
    <div className="rounded border border-border/50 overflow-hidden">
      <button
        onClick={onToggle}
        disabled={!match.duelId}
        className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm ${
          match.duelId ? "hover:bg-muted/30 cursor-pointer" : "cursor-default"
        }`}
        data-testid={`match-${match.id}`}
      >
        <span className={`flex-1 ${isWinA ? "font-semibold text-primary" : ""}`}>{nameA}</span>
        {isCompleted ? (
          <Badge variant={isDraw ? "outline" : "secondary"} className="text-xs tabular-nums">
            {match.scoreA}:{match.scoreB}
          </Badge>
        ) : match.status === "active" ? (
          <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
        <span className={`flex-1 text-right ${isWinB ? "font-semibold text-primary" : ""}`}>{nameB}</span>
        {match.duelId && (
          expanded ? <ChevronUp className="w-3 h-3 text-muted-foreground shrink-0" /> : <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
        )}
      </button>

      {/* Show assigned case under the match row */}
      {assignedCase && (
        <div className="px-3 pb-1.5 text-xs text-muted-foreground flex items-center gap-1.5">
          <BookOpen className="w-3 h-3" />
          {assignedCase.title}
          {assignedRp && <span className="text-muted-foreground/70">• {assignedRp.roleA} vs {assignedRp.roleB}</span>}
        </div>
      )}

      {expanded && duel && (duel as any).rounds && (
        <div className="border-t border-border/50 bg-muted/10 p-3 space-y-2 max-h-60 overflow-y-auto">
          {((duel as any).rounds || []).map((r: any) => (
            <div key={r.round} className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground">Раунд {r.round}</div>
              <div className="text-xs bg-background rounded p-2 space-y-1">
                <p><span className="font-medium">{botA?.name || "A"}:</span> {r.speakerA}</p>
                <p><span className="font-medium">{botB?.name || "B"}:</span> {r.speakerB}</p>
              </div>
            </div>
          ))}
          {(duel as any).judgeNotes && (
            <div className="text-xs text-muted-foreground italic border-t border-border/30 pt-2">
              Судья: {(duel as any).judgeNotes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Standings table ─── */

function StandingsTable({
  results,
  groups,
  botMap,
}: {
  results: TournamentStanding[];
  groups: TournamentGroup[];
  botMap: Map<string, Bot>;
}) {
  return (
    <Card className="p-5 space-y-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
        <Crown className="w-4 h-4" />
        Итоги чемпионата
      </h3>

      {groups.map(group => {
        const groupResults = results
          .filter(r => r.groupId === group.id)
          .sort((a, b) => b.points - a.points || (b.goalsFor - b.goalsAgainst) - (a.goalsFor - a.goalsAgainst));

        return (
          <div key={group.id} className="space-y-2">
            <h4 className="text-sm font-medium">{group.name}</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`standings-${group.id}`}>
                <thead>
                  <tr className="text-xs text-muted-foreground border-b border-border/50">
                    <th className="text-left py-2 pr-2">#</th>
                    <th className="text-left py-2">Участник</th>
                    <th className="text-center py-2 px-2">В</th>
                    <th className="text-center py-2 px-2">Н</th>
                    <th className="text-center py-2 px-2">П</th>
                    <th className="text-center py-2 px-2">Очки</th>
                    <th className="text-center py-2 px-2">GD</th>
                  </tr>
                </thead>
                <tbody>
                  {groupResults.map((s, i) => {
                    const bot = botMap.get(s.botId);
                    return (
                      <tr
                        key={s.botId}
                        className={`border-b border-border/30 ${i === 0 ? "bg-primary/5" : ""}`}
                        data-testid={`standing-row-${s.botId}`}
                      >
                        <td className="py-2 pr-2">
                          {i === 0 ? <Medal className="w-4 h-4 text-primary" /> : <span className="text-muted-foreground">{i + 1}</span>}
                        </td>
                        <td className="py-2 font-medium">
                          {bot ? `${bot.avatar} ${bot.name}` : s.botId}
                        </td>
                        <td className="text-center py-2 px-2 text-green-400">{s.wins}</td>
                        <td className="text-center py-2 px-2 text-muted-foreground">{s.draws}</td>
                        <td className="text-center py-2 px-2 text-red-400">{s.losses}</td>
                        <td className="text-center py-2 px-2 font-bold">{s.points}</td>
                        <td className="text-center py-2 px-2 tabular-nums text-xs text-muted-foreground">
                          {s.goalsFor - s.goalsAgainst > 0 ? "+" : ""}{s.goalsFor - s.goalsAgainst}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </Card>
  );
}

/* ─── Main page ─── */

export default function TournamentPage() {
  const [tab, setTab] = useState("list");

  const { data: tournaments, isLoading } = useQuery<Tournament[]>({
    queryKey: ["/api/tournaments"],
    staleTime: 0,
    refetchOnMount: "always",
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = (tournaments || []).find(t => t.id === selectedId);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Чемпионат</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Турниры ботов-переговорщиков в формате групповых этапов
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="list" data-testid="tab-tournament-list">
            <Trophy className="w-4 h-4 mr-1.5" />
            Турниры
          </TabsTrigger>
          <TabsTrigger value="create" data-testid="tab-tournament-create">
            <Plus className="w-4 h-4 mr-1.5" />
            Создать
          </TabsTrigger>
        </TabsList>

        <TabsContent value="create" className="mt-4">
          <CreateTournamentForm onCreated={() => setTab("list")} />
        </TabsContent>

        <TabsContent value="list" className="mt-4 space-y-4">
          {isLoading && (
            <Card className="p-8 text-center text-muted-foreground">
              <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
              <p className="text-sm">Загрузка...</p>
            </Card>
          )}

          {!isLoading && (tournaments || []).length === 0 && (
            <Card className="p-8 text-center text-muted-foreground">
              <Trophy className="w-8 h-8 mx-auto mb-3 opacity-50" />
              <p className="text-sm">Нет турниров</p>
              <p className="text-xs mt-1">Создайте первый чемпионат</p>
            </Card>
          )}

          {!selected ? (
            <div className="space-y-3">
              {(tournaments || []).map(t => (
                <Card
                  key={t.id}
                  className="p-4 flex items-center justify-between hover:border-primary/20 transition-colors cursor-pointer"
                  onClick={() => setSelectedId(t.id)}
                  data-testid={`tournament-card-${t.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Trophy className={`w-5 h-5 ${t.status === "completed" ? "text-primary" : "text-muted-foreground"}`} />
                    <div>
                      <h3 className="text-sm font-semibold">{t.name}</h3>
                      <p className="text-xs text-muted-foreground">
                        {t.groups.reduce((acc, g) => acc + g.participantIds.length, 0)} участников •{" "}
                        {t.groups.reduce((acc, g) => acc + g.matches.length, 0)} встреч
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant={t.status === "completed" ? "default" : t.status === "active" ? "secondary" : "outline"}
                    className={`text-xs ${t.status === "paused" ? "border-amber-500/40 text-amber-500" : ""}`}
                  >
                    {t.status === "setup"
                      ? "Подготовка"
                      : t.status === "active"
                        ? "В процессе"
                        : t.status === "paused"
                          ? "На паузе"
                          : "Завершён"}
                  </Badge>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedId(null)}
                className="text-xs gap-1"
                data-testid="btn-back-to-list"
              >
                ← Назад к списку
              </Button>
              <TournamentView tournament={selected} onDeleted={() => setSelectedId(null)} />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
